import React, { useState, useEffect, useContext, useMemo, useCallback, useRef } from 'react'
import ReactPlayer from 'react-player/lazy'
import Slider from 'react-slick'
import CustomCommonContext from '../../../context/common/commonContext'
import './Banner.css'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { detectFileType } from '@/product/common/components'
import BannerSkeletons from '../Loaders/BannerSkeletons'

const Banner = () => {
    const { allHomeBanner } = useContext(CustomCommonContext)
    const isMobile = useMediaQuery('(max-width:767px)')

    const [active, setActive] = useState([])
    const [isReady, setIsReady] = useState(false)

    const currentSlideRef = useRef(0)
    const activeRef = useRef([])

    useEffect(() => {
        activeRef.current = active
    }, [active])

    const NextArrow = useCallback(
        ({ onClick, fill = 'white' }) => (
            <button type="button" className="slick-next" onClick={onClick}>
                <svg
                    width="20"
                    height="30"
                    viewBox="0 0 7 10"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path d="M0 10H3.22L6.79 5L3.22 0H0L3.58 5L0 10Z" fill={fill} />
                </svg>
            </button>
        ),
        [],
    )

    const PrevArrow = useCallback(
        ({ onClick, fill = 'white' }) => (
            <button type="button" className="slick-prev" onClick={onClick}>
                <svg
                    width="20"
                    height="30"
                    viewBox="0 0 7 10"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M6.78906 10H3.56906L-0.000937462 5L3.56906 0H6.78906L3.20906 5L6.78906 10Z"
                        fill={fill}
                    />
                </svg>
            </button>
        ),
        [],
    )

    /* ---------------- Load Active Banners ---------------- */

    useEffect(() => {
        if (!allHomeBanner?.length) return

        const activeBanners = allHomeBanner
            .filter((item) => item.active === 1)
            .sort((a, b) => a.show_order - b.show_order)

        setActive(activeBanners)

        if (activeBanners.length > 0) {
            preloadFirstBanner(activeBanners[0])
        }
    }, [allHomeBanner, isMobile])

    /* ---------------- Preload First Banner ---------------- */

    const preloadFirstBanner = (banner) => {
        const basePath = `${global.site_url || ''}/uploads/banner/`

        const imageUrl =
            isMobile && banner.image_mobile
                ? `${basePath}compressed/${banner.image_mobile}`
                : `${basePath}${banner.image}`

        const fileType = detectFileType(imageUrl)

        if (fileType === 'video') {
            // For video just allow render immediately
            setIsReady(true)
            return
        }

        const img = new Image()
        img.src = imageUrl
        img.onload = () => {
            setIsReady(true)
        }
        img.onerror = () => {
            setIsReady(true)
        }
    }

    /* ---------------- Slider Settings ---------------- */

    const settingsBanner = useMemo(
        () => ({
            dots: false,
            fade: true,
            infinite: true,
            arrows: true,
            slidesToShow: 1,
            slidesToScroll: 1,
            autoplay: true,
            speed: 800,
            autoplaySpeed: 5000,
            initialSlide: 0, // starts from the first slide
            cssEase: 'ease-in-out',
            pauseOnHover: false,
            lazyLoad: false,
            nextArrow: <NextArrow />,
            prevArrow: <PrevArrow />,
            beforeChange: (current, next) => {
                // setCurrentSlide(next)
                currentSlideRef.current = next
            },
            responsive: [
                {
                    breakpoint: 1024,
                    settings: {
                        arrows: false,
                        infinite: true,
                        fade: true,
                        initialSlide: 0,
                    },
                },
            ],
        }),
        [NextArrow, PrevArrow],
    )

    /* ---------------- Click ---------------- */

    const handleBannerItemClick = useCallback(() => {
        const index = currentSlideRef.current
        const bannerItem = activeRef.current[index]

        console.log('banner click value', bannerItem, 'index:', index)
        if (!bannerItem) {
            console.warn('No banner item provided to handleBannerItemClick')
            return
        }

        const { link } = bannerItem

        if (!link) {
            console.warn('No link found for this banner (direct)')
            return
        }

        try {
            if (link.startsWith('http://') || link.startsWith('https://')) {
                window.open(link, '_blank')
            } else {
                const trimmedLink = link.trim()
                if (trimmedLink) {
                    window.open(`/${trimmedLink}`, '_blank')
                } else {
                    console.warn('Empty link after trim')
                }
            }
        } catch (error) {
            console.error('Error opening banner link (direct):', error)
        }
    }, [])

    /* ---------------- Render Slides ---------------- */

    const renderBannerItems = useMemo(() => {
        if (!active.length) return null

        return active.map((data, index) => {
            const basePath = `${global.site_url || ''}/uploads/banner/`

            const imageUrl =
                isMobile && data.image_mobile
                    ? `${basePath}${data.image_mobile}`
                    : `${basePath}${data.image}`

            const originalImageUrl =
                isMobile && data.image_mobile
                    ? `${basePath}${data.image_mobile}`
                    : `${basePath}${data.image}`
            const fileType = detectFileType(imageUrl)

            return (
                <div
                    key={data.id || index}
                    className="bannerInner"
                    onDoubleClick={handleBannerItemClick}
                >
                    {fileType === 'video' ? (
                        <ReactPlayer
                            className="react-player"
                            width="100%"
                            height="100%"
                            url={originalImageUrl}
                            playing
                            muted
                            loop
                            playsinline
                        />
                    ) : (
                        <img className="bannerImg" src={imageUrl} alt={`banner_${index}`} />
                    )}
                </div>
            )
        })
    }, [active, isMobile, handleBannerItemClick])

    /* ---------------- Final Render ---------------- */
    if (!isReady) {
        return <BannerSkeletons />
    }

    return (
        <div className="heroCarousel">
            <Slider {...settingsBanner}>{renderBannerItems}</Slider>
            {/* <div className={`bannerSkeleton ${isReady ? 'fade-out' : ''}`}>
                <BannerSkeletons />
            </div> */}
        </div>
    )
}

export default React.memo(Banner)
